import { useCallback, useEffect, useState } from 'react';
import {
  formatTimeZoneData,
  buildICSForSlot,
  buildGmailUrl,
  buildGCalUrlForSlot,
  writeTextToClipboard,
} from '../utils/meetingExport';

// Action modal state + handlers for time-slot clicks. Mirrors v1's modal:
// Add Meeting Option, Copy to Clipboard, Gmail, Google Calendar, iCal —
// all operating on whichever slot the user just clicked.
export function useSlotActions({ orderedCityRows, addMeetingOption }) {
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionModalPos, setActionModalPos] = useState({ x: 0, y: 0 });
  const [actionSlot, setActionSlot] = useState(null);

  // Outside click dismisses the modal.
  useEffect(() => {
    if (!showActionModal) return;
    const close = (e) => {
      if (!e.target.closest('.wv2-action-modal')) setShowActionModal(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showActionModal]);

  const handleSlotClick = useCallback((e, slot) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setActionModalPos({ x: r.left + window.scrollX, y: r.bottom + window.scrollY + 4 });
    setActionSlot(slot);
    setShowActionModal(true);
  }, []);

  const closeActionModal = useCallback(() => setShowActionModal(false), []);

  const handleCopyAction = useCallback(() => {
    const text = formatTimeZoneData(actionSlot, orderedCityRows);
    if (text) writeTextToClipboard(text);
    setShowActionModal(false);
  }, [actionSlot, orderedCityRows]);

  const handleGmailAction = useCallback(() => {
    const text = formatTimeZoneData(actionSlot, orderedCityRows);
    if (text) {
      window.open(buildGmailUrl({ body: text }), '_blank', 'noopener');
    }
    setShowActionModal(false);
  }, [actionSlot, orderedCityRows]);

  const handleGCalAction = useCallback(() => {
    if (!actionSlot) return;
    const details = formatTimeZoneData(actionSlot, orderedCityRows);
    window.open(buildGCalUrlForSlot(actionSlot, { details }), '_blank', 'noopener');
    setShowActionModal(false);
  }, [actionSlot, orderedCityRows]);

  const handleICalAction = useCallback(() => {
    if (!actionSlot) return;
    const description = formatTimeZoneData(actionSlot, orderedCityRows);
    const ics = buildICSForSlot(actionSlot, { description });
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `zonewise-meet-${actionSlot.dateTime.toFormat('yyyyMMdd-HHmm')}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    setShowActionModal(false);
  }, [actionSlot, orderedCityRows]);

  const handleAddMeetingOption = useCallback(() => {
    if (actionSlot) addMeetingOption(actionSlot.dateTime);
    setShowActionModal(false);
  }, [actionSlot, addMeetingOption]);

  return {
    showActionModal,
    actionSlot,
    actionModalPos,
    closeActionModal,
    handleSlotClick,
    handleCopyAction,
    handleGmailAction,
    handleGCalAction,
    handleICalAction,
    handleAddMeetingOption,
  };
}
